export const OSRM_TABLE_BASE = "https://router.project-osrm.org/table/v1/driving"
export const OSRM_ROUTE_DEMO_BASE = "https://router.project-osrm.org/route/v1/driving"

const TIMEOUT_TABLE_MS = 18000
const TIMEOUT_ROUTE_MS = 22000
const BLOCO_ROUTE = 25

function coordsQuery(pontos) {
	return pontos.map((p) => `${p.lo},${p.la}`).join(";")
}

export function montarUrlTable(pontos) {
	return `${OSRM_TABLE_BASE}/${coordsQuery(pontos)}?annotations=duration,distance&generate_hints=false`
}

export function montarUrlRoute(pontos) {
	return `${OSRM_ROUTE_DEMO_BASE}/${coordsQuery(pontos)}?geometries=geojson&overview=full&generate_hints=false`
}

function custoCelula(durations, distances, de, para) {
	const dur = Number(durations?.[de]?.[para])
	if (Number.isFinite(dur) && dur >= 0) return dur
	const dist = Number(distances?.[de]?.[para])
	if (Number.isFinite(dist) && dist >= 0) return dist
	return Number.POSITIVE_INFINITY
}

/**
 * `pontosEntrada[0]` é o profissional. Ordem = vizinho mais próximo na rua (duração).
 */
export function ordenarVizinhoMaisProximo(pontosEntrada, json) {
	const paradas = pontosEntrada.slice(1)
	const durations = json?.durations
	const distances = json?.distances
	if (!Array.isArray(paradas) || !paradas.length) return []
	if (!Array.isArray(durations) && !Array.isArray(distances)) {
		return paradas.map((p, i) => ({ ...p, parada: i + 1 }))
	}

	const n = pontosEntrada.length
	const visitado = new Array(n).fill(false)
	visitado[0] = true
	const ordem = []
	let atual = 0

	while (ordem.length < paradas.length) {
		let melhor = -1
		let melhorCusto = Number.POSITIVE_INFINITY
		for (let j = 1; j < n; j++) {
			if (visitado[j]) continue
			const custo = custoCelula(durations, distances, atual, j)
			if (custo < melhorCusto) {
				melhorCusto = custo
				melhor = j
			}
		}
		if (melhor < 0) {
			for (let j = 1; j < n; j++) {
				if (!visitado[j]) {
					visitado[j] = true
					ordem.push(pontosEntrada[j])
				}
			}
			break
		}
		visitado[melhor] = true
		ordem.push(pontosEntrada[melhor])
		atual = melhor
	}

	return ordem.map((p, i) => ({ ...p, parada: i + 1 }))
}

export function polylineDoRoute(json) {
	const coords = json?.routes?.[0]?.geometry?.coordinates
	if (!Array.isArray(coords)) return []
	return coords
		.filter((pt) => Array.isArray(pt) && pt.length >= 2 && Number.isFinite(Number(pt[0])) && Number.isFinite(Number(pt[1])))
		.map(([lon, lat]) => [Number(lat), Number(lon)])
}

export function metricasDoRoute(json) {
	const route = json?.routes?.[0]
	const proximo = route?.legs?.[0]
	return {
		distanciaKm: Number(route?.distance) / 1000 || 0,
		duracaoSeg: Number(route?.duration) || 0,
		duracaoProximoSeg: Number(proximo?.duration) || 0,
		distanciaProximoKm: Number(proximo?.distance) / 1000 || 0,
	}
}

async function fetchJson(url, timeoutMs, signalExterno) {
	const ctrl = new AbortController()
	const timer = setTimeout(() => ctrl.abort(), timeoutMs)
	const abortar = () => ctrl.abort()
	signalExterno?.addEventListener("abort", abortar)
	try {
		const res = await fetch(url, { signal: ctrl.signal })
		if (!res.ok) {
			const err = new Error(`HTTP ${res.status}`)
			err.status = res.status
			throw err
		}
		return res.json()
	} finally {
		clearTimeout(timer)
		signalExterno?.removeEventListener("abort", abortar)
	}
}

async function buscarTable(pontos, signal) {
	const json = await fetchJson(montarUrlTable(pontos), TIMEOUT_TABLE_MS, signal)
	if (json?.code !== "Ok") {
		const err = new Error(json?.message || json?.code || "Table indisponível")
		err.code = json?.code
		throw err
	}
	return json
}

function juntarPolylines(pedacos) {
	const line = []
	for (let i = 0; i < pedacos.length; i++) {
		const trecho = pedacos[i]
		if (!trecho.length) continue
		if (!line.length) {
			line.push(...trecho)
			continue
		}
		line.push(...trecho.slice(1))
	}
	return line
}

async function buscarRouteBloco(pontos, signal) {
	const json = await fetchJson(montarUrlRoute(pontos), TIMEOUT_ROUTE_MS, signal)
	if (json?.code !== "Ok") {
		const err = new Error(json?.message || json?.code || "Route indisponível")
		err.code = json?.code
		throw err
	}
	return json
}

async function tracarRotaDemo(pontos, signal) {
	if (!Array.isArray(pontos) || pontos.length < 2) {
		return { ok: false, polyline: [], metricas: {} }
	}

	try {
		const json = await buscarRouteBloco(pontos, signal)
		return { ok: true, polyline: polylineDoRoute(json), metricas: metricasDoRoute(json) }
	} catch (err) {
		if (pontos.length <= 2) throw err
	}

	const pedacos = []
	let metricas = {}
	for (let i = 0; i < pontos.length - 1; i += BLOCO_ROUTE - 1) {
		const bloco = pontos.slice(i, Math.min(i + BLOCO_ROUTE, pontos.length))
		if (bloco.length < 2) break
		const json = await buscarRouteBloco(bloco, signal)
		if (i === 0) metricas = metricasDoRoute(json)
		pedacos.push(polylineDoRoute(json))
	}

	const polyline = juntarPolylines(pedacos)
	return { ok: polyline.length > 1, polyline, metricas }
}

function numerar(paradas) {
	return paradas.map((p, i) => ({ ...p, parada: i + 1 }))
}

/**
 * Ordena no Table demo (vizinho mais próximo) e traça no Route demo.
 * `pontos[0]` = origem do profissional.
 */
export async function montarRotaTableRoute(pontos, signal) {
	if (!Array.isArray(pontos) || pontos.length < 2) {
		return { ok: false, pontos: pontos?.slice(1) ?? [], polyline: [], metricas: {} }
	}

	let ordenados
	try {
		const tabela = await buscarTable(pontos, signal)
		ordenados = ordenarVizinhoMaisProximo(pontos, tabela)
	} catch (err) {
		console.warn("[Minha rota] OSRM Table:", err)
		ordenados = numerar(pontos.slice(1))
	}

	if (!ordenados.length) {
		return { ok: false, pontos: [], polyline: [], metricas: {} }
	}

	try {
		const seq = [pontos[0], ...ordenados]
		const rota = await tracarRotaDemo(seq, signal)
		return {
			ok: rota.ok,
			pontos: ordenados,
			polyline: rota.polyline,
			metricas: rota.metricas || {},
		}
	} catch (err) {
		console.warn("[Minha rota] OSRM Route:", err)
		return { ok: false, pontos: ordenados, polyline: [], metricas: {} }
	}
}

/** Traça a polyline na sequência já definida (`pontos[0]` = origem). */
export async function tracarRotaNaOrdem(pontos, signal) {
	if (!Array.isArray(pontos) || pontos.length < 2) {
		return { ok: false, pontos: pontos?.slice(1) ?? [], polyline: [], metricas: {} }
	}
	const ordenados = numerar(pontos.slice(1))
	try {
		const rota = await tracarRotaDemo(pontos, signal)
		return {
			ok: rota.ok,
			pontos: ordenados,
			polyline: rota.polyline || [],
			metricas: rota.metricas || {},
		}
	} catch (err) {
		console.warn("[Minha rota] OSRM Route (ordem manual):", err)
		return { ok: false, pontos: ordenados, polyline: [], metricas: {} }
	}
}
